import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import TrialBanner from '../components/TrialBanner';
import UpgradeBanner from '../components/billing/UpgradeBanner';
import B2CDashboard from './B2CDashboard';
import {
  Users, BarChart2, Headphones, Radio,
  Users2, BookOpen, Bot, Building2, ArrowRight,
  CreditCard, MessageSquare, Megaphone, Shield, ScrollText, Zap, Gift,
  Send, TrendingUp
} from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import AppHeader from '../components/AppHeader';
import { theme } from '../styles/theme';
import {
  PageContainer, PageSubtitle,
  Grid, Flex, Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText
} from '../components/ui';
import api from '../services/api';

const TenantDashboard = () => {
  const { t } = useTranslation();
  const { tenantId } = useParams();

  const [tenant, setTenant]   = useState(null);
  const [agents, setAgents]   = useState([]);
  const [tierObj, setTierObj] = useState(null);
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  useEffect(() => { loadData(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [tenantRes, installedRes, tiersRes] = await Promise.all([
        api.get(`/tenants/${tenantId}`),
        api.get(`/marketplace/tenants/${tenantId}/agents`),
        api.get('/platform/tiers').then(r => r.data).catch(() => ({ tiers: [] })),
      ]);
      const tenantData = tenantRes.data;
      setTenant(tenantData);
      setAgents(installedRes.data.agents || []);
      const found = (tiersRes.tiers || []).find(t => t.tierId === tenantData.tier);
      setTierObj(found || null);
      // Salva audience no sessionStorage para AppLayout ler sem chamada extra
      const aud = found?.audience || 'business';
      sessionStorage.setItem(`tierAudience_${tenantId}`, aud);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // audience vem do tier object. 'personal' = B2C, qualquer outro = B2B
  const isPersonal = tierObj?.audience === 'personal';

  const BUSINESS_ACTIONS = [
    { to: 'agents-config',   icon: Bot,          label: t('nav.agents'),                                                    desc: t('nav.agentsDesc') },
    { to: 'campaigns',       icon: Megaphone,    label: t('campaigns.title', { ns: 'common' }),                             desc: t('campaigns.subtitle', { ns: 'common' }) },
    { to: 'tasks',           icon: Zap,          label: t('nav.tasks'),                                                     desc: t('nav.tasksDesc') },
    { to: 'knowledge',       icon: BookOpen,     label: t('nav.knowledge'),                                                 desc: t('nav.knowledgeDesc') },
    { to: 'users',           icon: Users,        label: t('nav.users'),                                                     desc: t('nav.usersDesc') },
    { to: 'team',            icon: Users2,       label: t('nav.team'),                                                      desc: t('nav.teamDesc') },
    { to: 'templates',       icon: MessageSquare,label: t('nav.templates'),                                                 desc: t('nav.templatesDesc') },
    { to: 'mktpl',           icon: MessageSquare,label: t('nav.mktpl'),                                                     desc: t('nav.mktplDesc') },
    { to: 'callcenter',      icon: Headphones,   label: t('nav.callCenter'),                                                desc: t('nav.callCenterDesc') },
    { to: 'monitor',         icon: Radio,        label: t('nav.monitor'),                                                   desc: t('nav.monitorDesc') },
    { to: 'manager',         icon: BarChart2,    label: t('nav.managerDashboard'),                                          desc: t('nav.managerDashboardDesc') },
    { to: 'billing',         icon: CreditCard,   label: t('nav.billing'),                                                   desc: t('nav.billingDesc') },
    { to: 'referral',        icon: Gift,         label: t('nav.referral',        { defaultValue: 'Indique e Ganhe' }),      desc: t('nav.referralDesc',        { defaultValue: 'Indique amigos e ganhe mensagens extras' }) },
    { to: 'agents-activity', icon: TrendingUp,   label: t('nav.agentsActivity',  { defaultValue: 'Atividade dos Agentes' }),desc: t('nav.agentsActivityDesc',  { defaultValue: 'Ações executadas pelos agentes' }) },
    { to: 'audit',           icon: Shield,       label: t('audit.title',         { ns: 'common' }),                        desc: t('audit.subtitle',          { ns: 'common' }) },
    { to: 'logs',            icon: ScrollText,   label: t('nav.logs'),                                                      desc: t('nav.logsDesc') },
  ];

  const PERSONAL_ACTIONS = [
    { to: 'agents-config', icon: Bot,          label: t('personal.myAgent',      { defaultValue: 'Meu Assistente' }),       desc: t('personal.myAgentDesc',    { defaultValue: 'Configurar personalidade e conectores' }) },
    { to: 'logs',          icon: Send,         label: t('personal.conversations', { defaultValue: 'Conversas' }),           desc: t('personal.conversationsDesc', { defaultValue: 'Histórico de mensagens' }) },
    { to: 'knowledge',     icon: BookOpen,     label: t('personal.knowledge',    { defaultValue: 'O que ele sabe' }),       desc: t('personal.knowledgeDesc',  { defaultValue: 'Documentos e informações do assistente' }) },
    { to: 'tasks',         icon: Zap,          label: t('personal.tasks',        { defaultValue: 'Tarefas Automáticas' }), desc: t('personal.tasksDesc',      { defaultValue: 'Ações que o assistente executa sozinho' }) },
    { to: 'billing',       icon: CreditCard,   label: t('nav.billing'),                                                     desc: t('nav.billingDesc') },
    { to: 'referral',      icon: Gift,         label: t('nav.referral',          { defaultValue: 'Indique e Ganhe' }),      desc: t('nav.referralDesc',        { defaultValue: 'Indique amigos e ganhe mensagens extras' }) },
  ];

  const QUICK_ACTIONS = isPersonal ? PERSONAL_ACTIONS : BUSINESS_ACTIONS;

  const breadcrumbItems = isSallusAdmin
    ? [
        { label: 'Libben Agents', path: '/sallus/dashboard' },
        { label: tenant?.name || tenantId }
      ]
    : [
        { label: tenant?.name || tenantId }
      ];

  const buildPath = (key) => {
    const map = {
      'agents-config': `/agents-config/${tenantId}`,
      users:         `/users/${tenantId}`,
      manager:       `/manager/${tenantId}`,
      callcenter:    `/callcenter/${tenantId}`,
      monitor:       `/monitor/${tenantId}`,
      team:          `/team/${tenantId}`,
      knowledge:     `/knowledge/${tenantId}`,
      billing:       `/billing/${tenantId}`,
      referral:      `/referral/${tenantId}`,
      templates:     `/templates/${tenantId}`,
      mktpl:         `/mktpl/${tenantId}`,
      tasks:         `/tasks/${tenantId}`,
      campaigns:     `/campaigns/${tenantId}`,
      audit:             `/audit/${tenantId}`,
      'agents-activity': `/agents-activity/${tenantId}`,
      logs:          `/logs/${tenantId}`,
    };
    return map[key];
  };

  return (
    <>
      <AppHeader />
      {tenant && <TrialBanner tenant={tenant} />}

      {loading ? (
        <PageContainer>
          <LoadingContainer><Spinner /><span>{t('loading')}</span></LoadingContainer>
        </PageContainer>
      ) : isPersonal ? (
        <PageContainer>
          <B2CDashboard
            tenantId={tenantId}
            tenant={tenant}
            agents={agents}
            tierObj={tierObj}
            onRefresh={loadData}
          />
        </PageContainer>
      ) : (
        <>
          <TenantBanner>
            <Flex gap={theme.spacing[3]}>
              <BannerIcon><Building2 size={20} /></BannerIcon>
              <div>
                <BannerName>{tenant?.name || tenantId}</BannerName>
                <BannerSub>ID: {tenantId}</BannerSub>
              </div>
            </Flex>
          </TenantBanner>

          <PageContainer>
            <UpgradeBanner tenantId={tenantId} />
            <Breadcrumb items={breadcrumbItems} />

            <SectionTitle>{t('dashboard.quickAccess')}</SectionTitle>
            <ActionsGrid>
              {QUICK_ACTIONS.map(({ to, icon: Icon, label, desc }) => (
                <ActionCard key={to} to={buildPath(to)}>
                  <ActionIconWrap><Icon size={20} strokeWidth={1.5} /></ActionIconWrap>
                  <ActionLabel>{label}</ActionLabel>
                  <ActionDesc>{desc}</ActionDesc>
                  <ArrowRight size={14} style={{ marginTop: 'auto', opacity: 0.4 }} />
                </ActionCard>
              ))}
            </ActionsGrid>

            <SectionTitle style={{ marginTop: theme.spacing[8] }}>{t('dashboard.activeAgents')}</SectionTitle>
            <PageSubtitle style={{ marginBottom: theme.spacing[5] }}>
              {t('dashboard.activeAgentsSubtitle')}
            </PageSubtitle>

            {agents.length === 0 ? (
              <EmptyState>
                <EmptyIcon><Bot size={48} strokeWidth={1} /></EmptyIcon>
                <EmptyText>{t('dashboard.noAgents')}</EmptyText>
              </EmptyState>
            ) : (
              <Grid min="260px">
                {agents.map(agent => (
                  <AgentCard key={agent.agentId} to={`/agents-config/${tenantId}`}>
                    <AgentEmoji>{agent.icon || '🤖'}</AgentEmoji>
                    <AgentName>{agent.name}</AgentName>
                    <AgentRole>{agent.role}</AgentRole>
                    <ConfigLink>{t('dashboard.configure')} <ArrowRight size={13} /></ConfigLink>
                  </AgentCard>
                ))}
              </Grid>
            )}
          </PageContainer>
        </>
      )}
    </>
  );
};

const TenantBanner = styled.div`
  background: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.border};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BannerIcon = styled.div`
  width: 36px; height: 36px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: ${theme.radii.md};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;

const BannerName = styled.div`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${p => p.$light ? theme.colors.white : theme.colors.textPrimary};
`;

const BannerSub = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${theme.spacing[3]};
  @media (max-width: 639px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ActionCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  text-decoration: none;
  transition: ${theme.transitions.base};
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const ActionIconWrap = styled.div`
  width: 36px; height: 36px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: ${theme.radii.md};
  display: flex; align-items: center; justify-content: center;
`;

const ActionLabel = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const ActionDesc = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const AgentCard = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[6]};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  text-decoration: none;
  text-align: center;
  transition: ${theme.transitions.base};
  &:hover { border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.md}; transform: translateY(-2px); }
`;

const AgentEmoji = styled.div`font-size: 48px; line-height: 1;`;

const AgentName = styled.div`
  font-size: ${theme.typography.lg};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const AgentRole = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
`;

const ConfigLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.medium};
  margin-top: ${theme.spacing[2]};
`;

export default TenantDashboard;
